import {
  findPaymentMilestoneHolders,
  getMultipleRecords,
  isDtoolsSiQuoteLocked,
  isQuoteDraftEditable,
  QUERY_KEYS,
  type PaymentMilestoneData,
  useChangesSummary,
  usePaymentMilestonesQuery,
  useQuoteDetailsQuery,
  useQuoteDetailsSummary,
  useQuoteQuery,
  useSalesOrderPaymentMilestonesQuery,
} from '@/lib/api'
import {
  executeDataverseChangeset,
  requireDataverseEtag,
  type DataverseChangesetOperation,
} from '@/lib/api/dataverse-changeset'
import { usePluginMutation } from '@/lib/plugins/use-plugin-mutation'
import { PaymentMilestoneStatus, QuoteType } from '@/lib/types'
import { getDefaultMilestones } from '@/lib/utils'
import { useQueryClient } from '@tanstack/react-query'
import { memo, useCallback, useEffect, useMemo } from 'react'
import {
  claimDefaultPaymentMilestoneCreate,
  isPaymentMilestoneWriteInFlight,
  releaseDefaultPaymentMilestoneCreate,
  resolveDefaultPaymentMilestoneIds,
} from '@/lib/payment-milestone-create-guard'
import { shouldUseDefaultPaymentMilestones } from '@/lib/payment-milestone-rules'
import {
  getPaymentMilestonePool,
  getReservedMilestones,
  sumMilestoneAmounts,
} from '@/lib/payment-milestone-replacement'
import { areMilestoneSourcesLoaded, planPaymentMilestoneSync } from '@/lib/payment-milestone-sync-plan'
import { publishProposalDataChanged } from '@/lib/proposal-data-sync'

export const PaymentMilestonePlugin = memo(function PaymentMilestonePlugin() {
  const queryClient = useQueryClient()
  const { data: quote } = useQuoteQuery()
  const { data: quoteDetails, dataUpdatedAt: quoteDetailsUpdatedAt } = useQuoteDetailsQuery()
  const { data: milestones, dataUpdatedAt: milestonesUpdatedAt } = usePaymentMilestonesQuery()
  const { data: quoteDetailsSummary, dataUpdatedAt: quoteDetailsSummaryUpdatedAt } = useQuoteDetailsSummary()
  const { data: changesSummary, dataUpdatedAt: changesSummaryUpdatedAt } = useChangesSummary()
  const { data: salesOrderMilestones, dataUpdatedAt: salesOrderMilestonesUpdatedAt } =
    useSalesOrderPaymentMilestonesQuery()
  const activeMilestones = useMemo(
    () => (milestones ?? []).filter((milestone) => milestone.statuscode !== PaymentMilestoneStatus.Inactive),
    [milestones]
  )

  const isDraft = isQuoteDraftEditable(quote)
  const isDtoolsSiLocked = isDtoolsSiQuoteLocked(quote)

  const summary = useMemo(
    () => (quote?.dsy_type === QuoteType.ChangeOrder ? changesSummary : quoteDetailsSummary),
    [quote?.dsy_type, changesSummary, quoteDetailsSummary]
  )
  const summaryUpdatedAt =
    quote?.dsy_type === QuoteType.ChangeOrder ? changesSummaryUpdatedAt : quoteDetailsSummaryUpdatedAt
  const sourceQueriesLoaded = areMilestoneSourcesLoaded({
    quoteType: quote?.dsy_type,
    salesOrderId: quote?.dsy_salesorder?.salesorderid,
    quoteDetailsUpdatedAt,
    milestonesUpdatedAt,
    summaryUpdatedAt,
    salesOrderMilestonesUpdatedAt,
  })
  const pool = useMemo(
    () =>
      getPaymentMilestonePool({
        quoteType: quote?.dsy_type,
        summaryTotal: summary.total,
        reservedTotal: sumMilestoneAmounts(getReservedMilestones(salesOrderMilestones, quote?.quoteid)),
      }),
    [quote?.dsy_type, quote?.quoteid, summary.total, salesOrderMilestones]
  )

  const { schedule: scheduleCreateDefaults, cancel: cancelCreateDefaults } = usePluginMutation({
    flushId: 'payment-milestone-create-defaults',
    fingerprint: () => [
      quoteDetailsUpdatedAt,
      milestonesUpdatedAt,
      quoteDetailsSummaryUpdatedAt,
      changesSummaryUpdatedAt,
      salesOrderMilestonesUpdatedAt,
    ],
    scopeKey: quote?.quoteid,
    mutationKey: ['plugin', 'payment-milestone', 'create-defaults'],
    isReady: () => sourceQueriesLoaded,
    notReadyMessage: 'Payment milestone summary is still loading. Please try activating again in a moment.',
    isDirty: () =>
      !!quote &&
      shouldUseDefaultPaymentMilestones(quote.dsy_type) &&
      !isDtoolsSiLocked &&
      !!quoteDetails &&
      !!milestones &&
      activeMilestones.length === 0 &&
      pool !== 0 &&
      sourceQueriesLoaded &&
      !!isDraft,
    mutationFn: async () => {
      if (
        !quote ||
        !shouldUseDefaultPaymentMilestones(quote.dsy_type) ||
        !quoteDetails ||
        !milestones ||
        activeMilestones.length > 0 ||
        pool === 0
      ) {
        throw new Error('Failed to create default payment milestones')
      }
      if (!claimDefaultPaymentMilestoneCreate(quote.quoteid)) return []

      try {
        const existingMilestones = await getMultipleRecords<
          Pick<PaymentMilestoneData, 'dsy_paymentmilestoneid' | 'statuscode'>
        >(
          'dsy_paymentmilestones',
          `?$select=dsy_paymentmilestoneid,statuscode&$filter=_dsy_quote_value eq '${quote.quoteid}'`
        )
        if (existingMilestones.some((milestone) => milestone.statuscode !== PaymentMilestoneStatus.Inactive)) return []

        const defaultMilestones = getDefaultMilestones(pool)
        const ids = await resolveDefaultPaymentMilestoneIds(
          quote.quoteid,
          defaultMilestones.map((milestone) => milestone.dsy_sequencenumber),
          new Set(existingMilestones.map((milestone) => milestone.dsy_paymentmilestoneid)),
          findPaymentMilestoneHolders
        )
        await executeDataverseChangeset(
          defaultMilestones.map((milestone, index) => ({
            method: 'PATCH' as const,
            entitySet: 'dsy_paymentmilestones' as const,
            id: ids[index],
            body: milestone,
            upsert: true,
          }))
        )
        return defaultMilestones
      } finally {
        releaseDefaultPaymentMilestoneCreate(quote.quoteid)
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.paymentMilestones })
      if (quote?.quoteid) publishProposalDataChanged(quote.quoteid)
    },
    onError: async (error) => {
      console.error('PaymentMilestonePlugin: ~ error:', error)
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.paymentMilestones })
    },
  })

  useEffect(() => {
    if (
      isDtoolsSiLocked ||
      !quote ||
      !shouldUseDefaultPaymentMilestones(quote.dsy_type) ||
      !quoteDetails ||
      !milestones ||
      activeMilestones.length > 0 ||
      pool === 0 ||
      !sourceQueriesLoaded ||
      !isDraft
    )
      return
    scheduleCreateDefaults(1000)
    return cancelCreateDefaults
  }, [
    quote,
    quoteDetails,
    activeMilestones,
    milestones,
    pool,
    sourceQueriesLoaded,
    isDraft,
    isDtoolsSiLocked,
    scheduleCreateDefaults,
    cancelCreateDefaults,
  ])

  const computeUpdatePlan = useCallback(() => {
    if (!quote || !quoteDetails || !milestones) return null
    if (isPaymentMilestoneWriteInFlight(quote.quoteid)) return null
    return planPaymentMilestoneSync({
      milestones,
      quoteType: quote.dsy_type,
      quoteStateCode: quote.statecode,
      quoteStatusCode: quote.statuscode,
      pool,
      defaultMilestones: getDefaultMilestones(pool),
    })
  }, [quote, quoteDetails, milestones, pool])

  const {
    isPending: isUpdatingPaymentMilestones,
    schedule,
    cancel,
  } = usePluginMutation({
    flushId: 'payment-milestone-update',
    fingerprint: () => [
      quoteDetailsUpdatedAt,
      milestonesUpdatedAt,
      quoteDetailsSummaryUpdatedAt,
      changesSummaryUpdatedAt,
      salesOrderMilestonesUpdatedAt,
    ],
    scopeKey: quote?.quoteid,
    mutationKey: ['plugin', 'payment-milestone', 'update'],
    isReady: () => sourceQueriesLoaded,
    notReadyMessage: 'Payment milestone summary is still loading. Please try activating again in a moment.',
    isDirty: () => !!isDraft && !isDtoolsSiLocked && computeUpdatePlan() !== null,
    mutationFn: async () => {
      // A write in flight re-fetches milestones when it ends, which re-schedules this.
      if (isPaymentMilestoneWriteInFlight(quote?.quoteid)) return []
      const plan = computeUpdatePlan()
      if (!plan) throw new Error('No payment milestones to update')

      const operations: DataverseChangesetOperation[] =
        plan.kind === 'delete'
          ? plan.milestones.map((milestone) => ({
              method: 'DELETE',
              entitySet: 'dsy_paymentmilestones',
              id: milestone.dsy_paymentmilestoneid,
              etag: requireDataverseEtag(milestone),
            }))
          : [
              ...plan.deletedMilestones.map((milestone) => ({
                method: 'DELETE' as const,
                entitySet: 'dsy_paymentmilestones' as const,
                id: milestone.dsy_paymentmilestoneid,
                etag: requireDataverseEtag(milestone),
              })),
              ...plan.updates.map((update) => ({
                method: 'PATCH' as const,
                entitySet: 'dsy_paymentmilestones' as const,
                id: update.dsy_paymentmilestoneid,
                etag: update.etag,
                body: { dsy_amount: update.dsy_amount },
              })),
            ]
      await executeDataverseChangeset(operations)
      return operations
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.paymentMilestones })
      if (quote?.quoteid) publishProposalDataChanged(quote.quoteid)
    },
    onError: async (error) => {
      console.error('PaymentMilestonePlugin: ~ error:', error)
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.paymentMilestones })
    },
  })

  useEffect(() => {
    if (
      isUpdatingPaymentMilestones ||
      isDtoolsSiLocked ||
      !quote ||
      !quoteDetails ||
      !milestones ||
      milestones.length === 0 ||
      !sourceQueriesLoaded ||
      !isDraft
    ) {
      return
    }
    schedule(750)
    return cancel
  }, [
    pool,
    isUpdatingPaymentMilestones,
    quote,
    quoteDetails,
    milestones,
    sourceQueriesLoaded,
    isDraft,
    isDtoolsSiLocked,
    schedule,
    cancel,
  ])

  return null
})
